import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { Sort } from '@angular/material/sort';
import { finalize } from 'rxjs';
import { DashboardService } from '../services/dashboard.service';
import { AlarmSummary, DashboardDevice, DashboardSnapshot, DeviceOperationalStatusKey } from '../models/dashboard.model';
import { StorageService } from '../services/storage.service';
import { parsePlatformTimestamp } from '../utils/company-date';

type StatusFilter = 'all' | DeviceOperationalStatusKey;
type DeviceColumn = 'serial' | 'status' | 'flags' | 'lastTelemetry' | 'worker' | 'project' | 'zone' | 'temperature' | 'battery';

const DEFAULT_DEVICE_COLUMNS: DeviceColumn[] = [
  'serial', 'status', 'flags', 'lastTelemetry', 'worker', 'project', 'zone', 'temperature', 'battery',
];
const REQUIRED_DEVICE_COLUMNS: DeviceColumn[] = ['serial', 'status'];

@Component({
  selector: 'app-dashboard-overview',
  templateUrl: './dashboard-overview.component.html',
  styleUrl: './dashboard-overview.component.css',
})
export class DashboardOverviewComponent implements OnInit, OnChanges {
  @Input() companyId?: number;
  @Input() projectId?: number;
  @Input() compact = false;

  snapshot: DashboardSnapshot | null = null;
  loading = false;
  error = false;
  acknowledging = new Set<string>();
  acknowledgementErrors = new Set<string>();
  criticalBannerExpanded = false;

  search = '';
  status: StatusFilter = 'all';
  selectedProject = 0;
  selectedZone = 0;
  selectedWorker = 0;
  pageIndex = 0;
  pageSize = 25;
  projects: Array<{ id: number; name: string }> = [];
  zones: Array<{ id: number; name: string }> = [];
  workers: Array<{ id: number; name: string }> = [];
  filteredDevices: DashboardDevice[] = [];
  pagedDevices: DashboardDevice[] = [];
  sort: Sort = { active: '', direction: '' };
  visibleColumns: DeviceColumn[] = [...DEFAULT_DEVICE_COLUMNS];
  readonly columnChoices: DeviceColumn[] = [...DEFAULT_DEVICE_COLUMNS];

  constructor(
    private dashboardService: DashboardService,
    private storageService: StorageService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.loadColumnPreferences();
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['companyId']?.firstChange || !changes['projectId']?.firstChange) this.load();
  }

  get effectiveCompanyId(): number {
    return Number(this.companyId || this.storageService.companyId);
  }

  get displayedColumns(): string[] {
    return [...this.visibleColumns, 'actions'];
  }

  get metrics(): Record<string, number> {
    const devices = this.snapshot?.devices ?? [];
    return {
      total: devices.length,
      alarm: devices.filter(device => device.operationalStatus === 'alarm').length,
      active: devices.filter(device => device.operationalStatus === 'active').length,
      hibernating: devices.filter(device => device.operationalStatus === 'hibernating').length,
    };
  }

  get visibleAlarms(): AlarmSummary[] {
    return (this.snapshot?.activeAlarms ?? [])
      .filter(alarm => alarm.status === 'active' && alarm.severity === 'critical')
  }

  get criticalAlarmCount(): number {
    return this.visibleAlarms.length;
  }

  get pendingAlarms(): AlarmSummary[] {
    return (this.snapshot?.activeAlarms ?? [])
      .filter(alarm => alarm.status === 'active')
      .sort((left, right) => Date.parse(right.detectedAt) - Date.parse(left.detectedAt));
  }

  load(): void {
    if ((!this.effectiveCompanyId && this.storageService.roleId !== 1) || this.loading) return;
    this.loading = true;
    this.error = false;
    const request = this.storageService.roleId === 1 && !this.companyId
      ? this.dashboardService.getGlobalSnapshot()
      : this.dashboardService.getSnapshot(this.effectiveCompanyId, this.projectId);
    request.pipe(
      finalize(() => this.loading = false),
    ).subscribe({
      next: snapshot => {
        this.snapshot = snapshot;
        if (!this.criticalAlarmCount) this.criticalBannerExpanded = false;
        this.pageIndex = 0;
        this.projects = this.uniqueOptions('project_id', 'project_name');
        this.zones = this.uniqueOptions('zone_id', 'zone_name');
        this.workers = this.uniqueOptions('user_id', 'user_name');
        this.applyFilters();
      },
      error: () => this.error = true,
    });
  }

  acknowledge(alarm: AlarmSummary): void {
    if (this.acknowledging.has(alarm.alarmId)) return;
    this.acknowledgementErrors.delete(alarm.alarmId);
    this.acknowledging.add(alarm.alarmId);
    this.dashboardService.acknowledgeAlarm(this.effectiveCompanyId, alarm.alarmId).pipe(
      finalize(() => this.acknowledging.delete(alarm.alarmId)),
    ).subscribe({
      next: () => {
        if (!this.snapshot) return;
        const activeAlarms = this.snapshot.activeAlarms.filter(item => item.alarmId !== alarm.alarmId);
        const devices = this.snapshot.devices.map(device => device.id === alarm.deviceId
          ? { ...device, activeAlarmCount: Math.max(0, device.activeAlarmCount - 1) }
          : device);
        this.snapshot = { ...this.snapshot, activeAlarms, devices };
        if (!this.criticalAlarmCount) this.criticalBannerExpanded = false;
        this.applyFilters();
      },
      error: () => this.acknowledgementErrors.add(alarm.alarmId),
    });
  }

  setStatus(status: StatusFilter): void {
    this.status = status;
    this.pageIndex = 0;
    this.applyFilters();
  }

  get hasActiveFilters(): boolean {
    return Boolean(this.search.trim() || this.status !== 'all' || this.selectedProject || this.selectedZone || this.selectedWorker);
  }

  resetFilters(): void {
    this.search = '';
    this.status = 'all';
    this.selectedProject = 0;
    this.selectedZone = 0;
    this.selectedWorker = 0;
    this.pageIndex = 0;
    this.applyFilters();
  }

  filtersChanged(): void {
    this.pageIndex = 0;
    this.applyFilters();
  }

  pageChanged(event: { pageIndex: number; pageSize: number }): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.updatePage();
  }

  sortChanged(sort: Sort): void {
    this.sort = sort;
    this.pageIndex = 0;
    this.applyFilters();
  }

  isColumnVisible(column: DeviceColumn): boolean {
    return this.visibleColumns.includes(column);
  }

  isRequiredColumn(column: DeviceColumn): boolean {
    return REQUIRED_DEVICE_COLUMNS.includes(column);
  }

  toggleColumn(column: DeviceColumn, visible: boolean): void {
    if (this.isRequiredColumn(column)) return;
    const selected = new Set(this.visibleColumns);
    visible ? selected.add(column) : selected.delete(column);
    this.visibleColumns = DEFAULT_DEVICE_COLUMNS.filter(item => selected.has(item));
    this.saveColumnPreferences();
  }

  restoreDefaultColumns(): void {
    this.visibleColumns = [...DEFAULT_DEVICE_COLUMNS];
    window.localStorage.removeItem(this.columnPreferenceKey);
  }

  openDevice(device: DashboardDevice | AlarmSummary): void {
    const deviceId = 'deviceId' in device ? device.deviceId : device.id;
    const companyId = 'deviceId' in device ? device.companyId : device.company_id;
    this.router.navigate(['/device'], { queryParams: { id: deviceId, companyId } });
  }

  openAlarmDevice(alarm: AlarmSummary): void {
    this.router.navigate(['/device'], {
      queryParams: { id: alarm.deviceId, companyId: alarm.companyId, alarmAt: alarm.detectedAt },
    });
  }

  toggleCriticalBanner(): void {
    this.criticalBannerExpanded = !this.criticalBannerExpanded;
  }

  alarmZone(alarm: AlarmSummary): string | null {
    return this.snapshot?.devices.find(device => device.id === alarm.deviceId)?.zone_name ?? null;
  }

  alarmIcon(alarm?: AlarmSummary): string {
    switch (alarm?.alarmType.toLowerCase()) {
      case 'fall':
      case 'caida': return 'personal_injury';
      case 'sos': return 'emergency';
      case 'unhooked':
      case 'desatado': return 'link_off';
      default: return 'notifications';
    }
  }

  trackAlarm(_: number, alarm: AlarmSummary): string {
    return alarm.alarmId;
  }

  statusClass(device: DashboardDevice): string {
    return `status-${device.operationalStatus}`;
  }

  statusLabel(device: DashboardDevice): string {
    return `dashboard.status.${device.operationalStatus}`;
  }

  telemetryAge(timestamp: string): { key: string; count: number } {
    const elapsedMinutes = Math.max(0, Math.floor(
      ((this.snapshot?.loadedAt.getTime() ?? Date.now()) - parsePlatformTimestamp(timestamp)) / 60_000,
    ));
    if (elapsedMinutes < 1) return { key: 'dashboard.telemetryAge.now', count: 0 };
    if (elapsedMinutes < 60) return { key: 'dashboard.telemetryAge.minutes', count: elapsedMinutes };
    const elapsedHours = Math.floor(elapsedMinutes / 60);
    if (elapsedHours < 24) return { key: 'dashboard.telemetryAge.hours', count: elapsedHours };
    return { key: 'dashboard.telemetryAge.days', count: Math.floor(elapsedHours / 24) };
  }

  private uniqueOptions(idKey: keyof DashboardDevice, nameKey: keyof DashboardDevice): Array<{ id: number; name: string }> {
    const options = new Map<number, string>();
    (this.snapshot?.devices ?? []).forEach(device => {
      const id = Number(device[idKey]);
      const name = String(device[nameKey] ?? '');
      if (id > 0 && name) options.set(id, name);
    });
    return [...options.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }

  private applyFilters(): void {
    const query = this.search.trim().toLowerCase();
    const devices = (this.snapshot?.devices ?? []).filter(device => {
      const matchesSearch = !query || [device.serial_number, device.user_name, device.project_name, device.zone_name]
        .some(value => value?.toLowerCase().includes(query));
      const matchesStatus = this.status === 'all'
        || device.operationalStatus === this.status;
      return matchesSearch
        && matchesStatus
        && (!this.selectedProject || Number(device.project_id) === this.selectedProject)
        && (!this.selectedZone || Number(device.zone_id) === this.selectedZone)
        && (!this.selectedWorker || Number(device.user_id) === this.selectedWorker);
    });
    this.filteredDevices = devices.sort((left, right) => this.compareDevices(left, right));
    this.updatePage();
  }

  private compareDevices(left: DashboardDevice, right: DashboardDevice): number {
    if (this.sort.active && this.sort.direction) {
      const direction = this.sort.direction === 'asc' ? 1 : -1;
      const result = this.compareValues(this.sortValue(left, this.sort.active), this.sortValue(right, this.sort.active));
      if (result) return direction * result;
    }
    const priority = this.operationalPriority(left) - this.operationalPriority(right);
    return priority || this.compareValues(left.serial_number, right.serial_number) || left.id - right.id;
  }

  private sortValue(device: DashboardDevice, column: string): string | number {
    if (column === 'status') return this.operationalPriority(device);
    if (column === 'serial') return device.serial_number;
    if (column === 'flags') return device.telemetryStatus.flags.join(',');
    if (column === 'lastTelemetry') return device.telemetryStatus.lastTelemetryAt || '';
    if (column === 'worker') return device.user_name || '';
    if (column === 'project') return device.project_name || '';
    if (column === 'zone') return device.zone_name || '';
    if (column === 'temperature') return device.temperature ?? -Infinity;
    if (column === 'battery') return device.battery ?? -Infinity;
    return '';
  }

  private operationalPriority(device: DashboardDevice): number {
    return ({ alarm: 0, 'no-data': 1, active: 2, hibernating: 3 } as const)[device.operationalStatus];
  }

  private compareValues(left: string | number, right: string | number): number {
    if (typeof left === 'number' && typeof right === 'number') return left - right;
    return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
  }

  private get columnPreferenceKey(): string {
    return `jimm.overview.columns.v1.${this.storageService.userId || 'anonymous'}`;
  }

  private loadColumnPreferences(): void {
    try {
      const stored = JSON.parse(window.localStorage.getItem(this.columnPreferenceKey) || 'null');
      if (!Array.isArray(stored)) return;
      const selected = new Set(stored.filter((column): column is DeviceColumn => DEFAULT_DEVICE_COLUMNS.includes(column)));
      REQUIRED_DEVICE_COLUMNS.forEach(column => selected.add(column));
      this.visibleColumns = DEFAULT_DEVICE_COLUMNS.filter(column => selected.has(column));
    } catch {
      window.localStorage.removeItem(this.columnPreferenceKey);
    }
  }

  private saveColumnPreferences(): void {
    window.localStorage.setItem(this.columnPreferenceKey, JSON.stringify(this.visibleColumns));
  }

  private updatePage(): void {
    const start = this.pageIndex * this.pageSize;
    this.pagedDevices = this.filteredDevices.slice(start, start + this.pageSize);
  }
}
